/**
 * Highlights search terms in text and provides utilities for search result formatting
 */

export interface HighlightedText {
    text: string;
    isHighlighted: boolean;
}

export interface ContentSnippet {
    snippet: string;
    highlightedSnippet: HighlightedText[];
    hasMoreBefore: boolean;
    hasMoreAfter: boolean;
}

/**
 * Escapes special regex characters in a string
 */
function escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Highlights search terms in text
 */
export function highlightText(text: string, searchTerm: string): HighlightedText[] {
    if (!searchTerm.trim()) {
        return [{ text, isHighlighted: false }];
    }

    const escapedTerm = escapeRegExp(searchTerm.trim());
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts
        .filter(part => part !== '') // Remove empty strings
        .map((part, index) => ({
            text: part,
            isHighlighted: regex.test(part),
        }));
}

/**
 * Checks if a string contains the search term (case-insensitive)
 */
export function containsSearchTerm(text: string, searchTerm: string): boolean {
    if (!searchTerm.trim()) return false;
    return text.toLowerCase().includes(searchTerm.toLowerCase());
}

/**
 * Extracts text content from HTML string
 */
export function extractTextFromHTML(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
}

/**
 * Extracts relevant snippets around search matches from HTML content
 */
export function extractContentSnippets(
    htmlContent: string, 
    searchTerm: string, 
    snippetLength = 150
): ContentSnippet[] {
    const textContent = extractTextFromHTML(htmlContent);
    
    if (!containsSearchTerm(textContent, searchTerm)) {
        return [];
    }

    const escapedTerm = escapeRegExp(searchTerm.trim());
    const regex = new RegExp(escapedTerm, 'gi');
    const matches = Array.from(textContent.matchAll(regex));
    
    const snippets: ContentSnippet[] = [];
    
    matches.forEach((match) => {
        const matchStart = match.index!;
        const matchEnd = matchStart + match[0].length;
        
        // Calculate snippet boundaries
        const snippetStart = Math.max(0, matchStart - Math.floor(snippetLength / 2));
        const snippetEnd = Math.min(textContent.length, matchEnd + Math.floor(snippetLength / 2));
        
        // Find word boundaries to avoid cutting words
        let adjustedStart = snippetStart;
        let adjustedEnd = snippetEnd;
        
        if (adjustedStart > 0) {
            const spaceIndex = textContent.lastIndexOf(' ', adjustedStart);
            if (spaceIndex > adjustedStart - 20) {
                adjustedStart = spaceIndex + 1;
            }
        }
        
        if (adjustedEnd < textContent.length) {
            const spaceIndex = textContent.indexOf(' ', adjustedEnd);
            if (spaceIndex !== -1 && spaceIndex < adjustedEnd + 20) {
                adjustedEnd = spaceIndex;
            }
        }
        
        const snippet = textContent.slice(adjustedStart, adjustedEnd);
        const highlightedSnippet = highlightText(snippet, searchTerm);
        
        snippets.push({
            snippet,
            highlightedSnippet,
            hasMoreBefore: adjustedStart > 0,
            hasMoreAfter: adjustedEnd < textContent.length,
        });
    });
    
    // Remove duplicate snippets (overlapping)
    return snippets.filter((snippet, index) => !snippets.slice(0, index).some(
        prevSnippet => prevSnippet.snippet.includes(snippet.snippet) 
            || snippet.snippet.includes(prevSnippet.snippet)
    ));
} 